import { motion } from 'framer-motion';
import { MessagesSquare } from 'lucide-react';
import type { MessageListItemDto } from '@cc/types';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import { relativeTime } from '@/lib/format';
import {
  CLASSIFICATION_META,
  TAG_LABELS,
  priorityStripeClass,
  avatarColor,
  avatarInitial,
} from '@/lib/inbox';

interface Props {
  message: MessageListItemDto;
  selected: boolean;
  onSelect: () => void;
  compact?: boolean;
  /** When > 1, this row represents the latest of a multi-message thread. */
  threadCount?: number;
}

export function MessageRow({ message, selected, onSelect, compact, threadCount }: Props) {
  const meta = CLASSIFICATION_META[message.classification];
  const stripe = priorityStripeClass(message.triagePriority);

  return (
    <motion.button
      type="button"
      onClick={onSelect}
      variants={{
        hidden: { opacity: 0, y: 3 },
        visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 160, damping: 18 } },
      }}
      className={cn(
        'group flex w-full gap-3 rounded-md border bg-card pl-0 pr-3 py-2.5 text-left transition-colors',
        'border-l-2',
        stripe,
        selected ? 'border-foreground/40 bg-accent/30' : 'border-border hover:border-foreground/20',
        !message.isRead && !selected && 'bg-accent/15',
      )}
    >
      <Avatar message={message} />
      <div className="min-w-0 flex-1 py-0.5">
        <div className="flex items-center gap-2">
          <p
            className={cn(
              'truncate text-sm',
              message.isRead ? 'font-normal text-foreground/90' : 'font-medium',
            )}
          >
            {message.fromName || message.fromEmail}
          </p>
          <Badge variant={meta.variant}>{meta.label}</Badge>
          {message.triagePriority && message.triagePriority >= 4 && (
            <Badge variant="warning">P{message.triagePriority}</Badge>
          )}
          {threadCount && threadCount > 1 && (
            <Badge variant="muted" className="gap-1 text-[10px]">
              <MessagesSquare className="h-3 w-3" /> {threadCount}
            </Badge>
          )}
          {message.projectName && !compact && (
            <Badge variant="outline">{message.projectName}</Badge>
          )}
        </div>
        <p className="mt-0.5 truncate text-sm text-foreground/85">
          {message.subject || '(no subject)'}
        </p>
        {!compact && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{message.snippet}</p>
        )}
        {message.tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {message.tags.map((t) => (
              <Badge key={t} variant="outline" className="text-[10px]">
                {TAG_LABELS[t] ?? t}
              </Badge>
            ))}
          </div>
        )}
      </div>
      <div className="flex-none py-0.5 text-right text-[11px] text-muted-foreground">
        <p>{relativeTime(message.receivedAt)}</p>
        <p className="mt-0.5 truncate">{message.accountEmail.split('@')[0]}</p>
      </div>
    </motion.button>
  );
}

function Avatar({ message }: { message: MessageListItemDto }) {
  return (
    <div
      className="mt-0.5 ml-3 flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-medium text-white"
      style={{ background: avatarColor(message.fromEmail) }}
      aria-hidden
    >
      {avatarInitial(message)}
    </div>
  );
}
